import React from "react";
import { X, Eye, RotateCcw, Star } from "lucide-react";

// ── Trigger pill config ────────────────────────────────────────────────────────
const TRIGGER_PILLS = {
  ai_generated:   { label: "AI",      className: "bg-fill-canvas-accent/15 text-ink-canvas-accent" },
  manual_edit:    { label: "Manual",  className: "bg-fill-gray-500/15 text-ink-gray-500 dark:text-ink-gray-400" },
  inline_ai_edit: { label: "AI Edit", className: "bg-fill-hex-f3e8ff dark:bg-fill-hex-581c87/30 text-ink-purple-600 dark:text-ink-hex-c084fc" },
};

function getPill(trigger) {
  if (trigger?.startsWith("restored_from_v")) {
    const n = trigger.replace("restored_from_v", "");
    return { label: `Restored v${n}`, className: "bg-fill-hex-fef3c7 dark:bg-fill-hex-78350f/30 text-ink-amber-700 dark:text-ink-amber-400" };
  }
  return TRIGGER_PILLS[trigger] ?? { label: trigger ?? "—", className: "bg-fill-gray-100 dark:bg-fill-neutral-surface-dark text-ink-gray-500 dark:text-ink-gray-400" };
}

function formatTs(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return (
    d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
    " · " +
    d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
  );
}

/**
 * ArtifactVersionHistory — slide-in version history panel for NebulaCanvas.
 *
 * Props:
 *   versions          — [{ version_number, saved_at, trigger }] ascending
 *   isLoading         — versions list still loading
 *   browsingVersion   — version_number currently being browsed, or null
 *   isLoadingVersion  — content for a specific version is being fetched
 *   pinnedVersion     — version_number that is currently pinned, or null
 *   onBrowse(n)       — start browsing version n
 *   onExitBrowse()    — exit browse mode (back to current code)
 *   onRestore(n)      — restore version n into the editor
 *   onPin(n)          — toggle pin on version n
 *   onClose()         — close the panel entirely
 */
export default function ArtifactVersionHistory({
  versions,
  isLoading,
  browsingVersion,
  isLoadingVersion,
  pinnedVersion,
  onBrowse,
  onExitBrowse,
  onRestore,
  onPin,
  onClose,
}) {
  // Show newest first
  const sorted = [...(versions ?? [])].reverse();

  return (
    <div className="flex flex-col max-h-[420px] bg-fill-white dark:bg-fill-page-surface-dark rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-edge-gray-200 dark:border-edge-panel-surface flex-shrink-0">
        <span className="text-xs font-semibold text-ink-ink dark:text-ink-white">Version history</span>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-fill-gray-100 dark:hover:bg-fill-panel-surface text-ink-gray-500 dark:text-ink-gray-400 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Browsing banner */}
      {browsingVersion != null && (
        <div className="flex items-center justify-between gap-2 px-3 py-1.5 bg-fill-canvas-accent/10 border-b border-edge-canvas-accent/20 flex-shrink-0">
          <span className="text-xs text-ink-canvas-accent font-medium truncate">Browsing v{browsingVersion}</span>
          <button
            onClick={onExitBrowse}
            className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-fill-canvas-accent/20 text-ink-canvas-accent hover:bg-fill-canvas-accent/30 transition-colors"
          >
            Exit
          </button>
        </div>
      )}

      {/* Version list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-16 text-xs text-ink-gray-500 dark:text-ink-gray-400">
            Loading…
          </div>
        ) : sorted.length === 0 ? (
          <div className="flex items-center justify-center h-16 text-xs text-ink-gray-500 dark:text-ink-gray-400">
            No versions yet
          </div>
        ) : (
          sorted.map((v, idx) => {
            const pill = getPill(v.trigger);
            const isBrowsing = browsingVersion === v.version_number;
            const isLatest = idx === 0;
            return (
              <div
                key={v.version_number}
                className={`flex flex-col gap-1 px-3 py-2 border-b border-edge-gray-100 dark:border-edge-panel-surface last:border-0 ${
                  isBrowsing ? "bg-fill-canvas-accent/5" : "hover:bg-fill-gray-50 dark:hover:bg-fill-hex-222222"
                }`}
              >
                {/* Version number + trigger pill */}
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold text-ink-ink dark:text-ink-white">
                    v{v.version_number}
                  </span>
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${pill.className}`}>
                    {pill.label}
                  </span>
                </div>

                {/* Timestamp */}
                <span className="text-[10px] text-ink-gray-400">{formatTs(v.saved_at)}</span>

                {/* #14 \u2014 user prompt that produced this version (inline AI edits only) */}
                {v.prompt && (
                  <span
                    className="text-[10px] italic text-ink-gray-500 dark:text-ink-gray-400 line-clamp-2 leading-snug"
                    title={v.prompt}
                  >
                    “{v.prompt}”
                  </span>
                )}

                {/* Action buttons — hidden for the current (latest) version */}
                <div className="flex items-center gap-1.5 mt-0.5">
                  {isLatest ? (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-fill-brand-openai/10 text-ink-brand-openai">
                      Current
                    </span>
                  ) : (
                    <>
                      <button
                        disabled={isLoadingVersion}
                        onClick={() => (isBrowsing ? onExitBrowse() : onBrowse(v.version_number))}
                        className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded transition-colors disabled:opacity-50 ${
                          isBrowsing
                            ? "bg-fill-canvas-accent/20 text-ink-canvas-accent"
                            : "bg-fill-gray-100 dark:bg-fill-panel-surface text-ink-gray-500 dark:text-ink-gray-400 hover:bg-fill-gray-200 dark:hover:bg-fill-neutral-surface-dark"
                        }`}
                      >
                        <Eye className="w-2.5 h-2.5" />
                        {isBrowsing ? "Exit" : "Browse"}
                      </button>
                      <button
                        disabled={isLoadingVersion}
                        onClick={() => onRestore(v.version_number)}
                        title="Restore this version"
                        className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-fill-gray-100 dark:bg-fill-panel-surface text-ink-gray-500 dark:text-ink-gray-400 hover:bg-fill-gray-200 dark:hover:bg-fill-neutral-surface-dark transition-colors disabled:opacity-50 border-[0.5px] border-edge-border dark:border-edge-strong-border"
                      >
                        <RotateCcw className="w-2.5 h-2.5" />
                        Restore
                      </button>
                    </>
                  )}
                  {onPin && (
                    <button
                      title={pinnedVersion === v.version_number ? "Unpin version" : "Pin as canonical"}
                      onClick={() => onPin(v.version_number)}
                      className={`p-0.5 rounded transition-colors ${
                        pinnedVersion === v.version_number
                          ? "text-ink-amber-400 hover:text-ink-amber-500"
                          : "text-ink-gray-300 dark:text-ink-gray-600 hover:text-ink-amber-400 dark:hover:text-ink-amber-400"
                      }`}
                    >
                      <Star
                        className="w-3 h-3"
                        fill={pinnedVersion === v.version_number ? "currentColor" : "none"}
                      />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
